import React from 'react';
import dayjs from 'dayjs';
import {
  IResourceComponentsProps,
  useNavigation,
  useList,
  useNotification,
} from '@pankod/refine-core';
import {
  Breadcrumbs,
  DataGrid,
  GridActionsCellItem,
  GridColumns,
  Link,
  List,
  Typography,
} from '@pankod/refine-mui';
import { fbFirestore } from 'lib/firebaseService';
import { collection, getDocs, doc, query, runTransaction, where } from "firebase/firestore";
import { Close, Edit } from '@mui/icons-material';
import { IGetDeviceUsings } from 'interfaces';
import { useSearchParams } from '@pankod/refine-react-router-v6';

export const DeviceUsingList: React.FC<
  IResourceComponentsProps
> = () => {
  const [params] = useSearchParams();
  const notification = useNotification();
  const { edit } = useNavigation();
  const code = params.get('code');
  const { refetch, data: response } = useList<IGetDeviceUsings>({
    resource: 'device_usings',
    config: {
      filters: [
        {
          field: 'code',
          operator: 'eq',
          value: code,
        },
      ],
    },
  });

  const resetDevice = (row: IGetDeviceUsings) => async () => {
    try {
      await runTransaction(fbFirestore, async (transaction) => {
        const offerQuery = query(collection(fbFirestore, "Offers"), where("code", "==", code));
        const offers = await getDocs(offerQuery);

        if (offers.empty) {
          throw new Error("Offer not found");
        }

        // Read the existing document
        const offerRef = offers.docs[0].ref;
        const offer = await transaction.get(offerRef);
        const existingDocRef = doc(fbFirestore, "device_usings", row.id);
        const existingDocSnap = await transaction.get(existingDocRef);

        if (!existingDocSnap.exists()) {
          throw new Error("Device not found");
        }

        // Perform the update
        await transaction
          .delete(existingDocRef)
          .update(offerRef, {
            use: offer.data()?.use - 1,
          });


        notification?.open?.({
          type: 'success',
          message: 'Device deleted',
        });
      });

      refetch();
    } catch (e) {
      notification?.open?.({
        type: 'error',
        message: (e as Error)?.message,
      })
    }
  };

  const columns = React.useMemo<GridColumns<IGetDeviceUsings>>(
    () => [
      {
        field: 'code',
        headerName: 'Code',
        flex: 1,
        minWidth: 100,
        disableReorder: true,
        sortable: true,
      },
      {
        field: 'device',
        headerName: 'Device',
        flex: 1,
        minWidth: 100,
        disableReorder: true,
        sortable: true,
      },
      {
        field: 'created_at',
        headerName: 'Created at',
        flex: 1,
        minWidth: 100,
        disableReorder: true,
        sortable: true,
        renderCell: function render({ row }) {
          return (
            <>
              {dayjs(row.created_at.toDate()).format(
                'YYYY-MM-DD HH:mm:ss'
              )}
            </>
          );
        },
      },
      {
        field: 'last_use',
        headerName: 'Last use',
        flex: 1,
        minWidth: 100,
        disableReorder: true,
        sortable: true,
        renderCell: function render({ row }) {
          return (
            <>
              {row.last_use
                ? dayjs(row.last_use.toDate()).format('YYYY-MM-DD HH:mm:ss')
                : ''
              }
            </>
          );
        },
      },
      {
        field: 'expiry_date',
        headerName: 'Expiry Date',
        flex: 1,
        minWidth: 100,
        disableReorder: true,
        sortable: true,
        renderCell: function render({ row }) {
          return (
            <>
              {row.expiry_date
                ? dayjs(row.expiry_date.toDate()).format('YYYY-MM-DD HH:mm:ss')
                : 'Same as offer'
              }
            </>
          );
        },
      },
      {
        field: 'actions',
        headerName: '#',
        type: 'actions',
        width: 40,
        getActions: function render({ row }) {
          return [
            <GridActionsCellItem
              key={1}
              label="Edit"
              icon={<Edit color="success" />}
              onClick={() => edit('device_usings', row.id)}
              showInMenu
            />,
            <GridActionsCellItem
              key={2}
              label="Delete"
              icon={<Close color="error" />}
              onClick={resetDevice(row)}
              showInMenu
            />,
          ];
        },
      }
    ],
    []
  );

  return (
    <>
      <List
        title={`Device usings (Code = ${code})`}
        canCreate={false}
        resource="device_usings"
        cardProps={{
          sx: { paddingX: { xs: 0, md: 0 }, position: 'relative' },
        }}
        breadcrumb={
          <Breadcrumbs sx={{ paddingLeft: '16px', paddingTop: '16px' }}>
            <Link underline="hover" color="inherit" href="/Offers">
              Offers
            </Link>
            <Typography sx={{ color: 'text.primary' }}>Device Usings</Typography>
          </Breadcrumbs>
        }
      >
        <DataGrid
          rows={response?.data || []}
          columns={columns}
          autoHeight
          disableColumnFilter={true}
          disableColumnSelector={true}
          rowsPerPageOptions={[5, 10, 15, 20]}
          density="comfortable"
          sx={{
            '& .MuiDataGrid-cell:hover': {
              cursor: 'pointer',
            },
          }}
        />
      </List>
    </>
  );
};
